import { P as PubSub } from './pubsub-CoPYzvVm.js';
import './notyf-DIw0RvBj.js';
import { f as fetchHTML } from './fetchHTML-CGNR3V1z.js';
import './parseHTML-Om2c1Ayj.js';

/**
 * @typedef {{ id: number; option1: string | null; option2: string | null; option3: string | null; available: boolean }} ShopVariant
 */

class ProductVariantSelect extends HTMLElement {
  #abortController;
  #url = this.dataset.url;
  #sectionId = this.dataset.sectionId;
  #productId = this.dataset.productId;
  /** @type {ShopVariant[]} */
  #variants = [];

  connectedCallback() {
    const script = this.querySelector('script[data-product-variants]');
    if (script?.textContent) {
      try {
        this.#variants = JSON.parse(script.textContent);
      } catch {
        this.#variants = [];
      }
    }

    this.addEventListener('change', (event) => {
      const target = event.target.closest('input[type="radio"][data-option-position]');
      if (target && !this.#abortController) this.#handleVariantChange({ target });
    });
  }

  /**
   * Get the selected options from the UI
   * @returns {Map<number, string>}
   */
  #getSelectedOptions() {
    const map = new Map();
    for (const input of this.querySelectorAll('input[data-option-position]:checked')) {
      const pos = Number(input.dataset.optionPosition);
      if (Number.isFinite(pos)) map.set(pos, input.value);
    }
    return map;
  }

  /**
   * Find the exact variant that matches the selected options
   * @param {Map<number, string>} selectionMap
   * @returns {ShopVariant | undefined}
   */
  #findExactVariant(selectionMap) {
    return this.#variants.find((v) => {
      for (const [pos, val] of selectionMap) {
        if (v[`option${pos}`] !== val) return false;
      }
      return true;
    });
  }

  /**
   * Pick the best candidate from the candidates array
   * @param {ShopVariant[]} candidates
   * @param {Map<number, string>} selectionMap
   * @param {number} changedPosition
   */
  #pickBestCandidate(candidates, selectionMap, changedPosition) {
    if (!candidates.length) return undefined;

    let best;
    let bestScore = -1;
    for (const v of candidates) {
      let score = 0;
      for (const [pos, val] of selectionMap) {
        if (pos === changedPosition) continue;
        if (v[`option${pos}`] === val) score++;
      }
      if (score > bestScore) {
        bestScore = score;
        best = v;
      }
    }
    return best;
  }

  /**
   * @param {ShopVariant} variant
   * @param {Map<number, string>} selectionMap
   * @returns {number}
   */
  #countMatchingOptions(variant, selectionMap) {
    let score = 0;
    for (const [pos, val] of selectionMap) {
      if (variant[`option${pos}`] === val) score++;
    }
    return score;
  }

  /**
   * When two variants tie on match count, prefer the available one.
   * @param {ShopVariant} candidate
   * @param {ShopVariant | undefined} current
   */
  #isCurrentAvailable(candidate, current) {
    return Boolean(current && candidate.available && !current.available);
  }

  /**
   * Find the closest variant that matches the selected options
   * @param {Map<number, string>} selectionMap
   */
  #findClosestVariant(selectionMap) {
    let best;
    let bestScore = -1;
    for (const v of this.#variants) {
      const score = this.#countMatchingOptions(v, selectionMap);
      if (score > bestScore) {
        bestScore = score;
        best = v;
      } else if (score === bestScore && this.#isCurrentAvailable(v, best)) {
        best = v;
      }
    }
    return best;
  }

  /**
   * Resolve a variant from the current UI selection. If the exact combo does not exist,
   * prefer variants that keep the option the shopper just changed, then best partial match.
   *
   * @param {Map<number, string>} selectionMap
   * @param {number} changedPosition
   */
  #resolveVariant(selectionMap, changedPosition) {
    const exact = this.#findExactVariant(selectionMap);
    if (exact) return exact;

    const changedVal = selectionMap.get(changedPosition);

    if (changedVal !== undefined) {
      const candidates = this.#variants.filter((v) => v[`option${changedPosition}`] === changedVal);
      const picked = this.#pickBestCandidate(candidates, selectionMap, changedPosition);
      if (picked) return picked;
    }

    return this.#findClosestVariant(selectionMap);
  }

  /**
   * @param {ShopVariant} variant
   */
  #syncInputsToVariant(variant) {
    for (let pos = 1; pos <= 3; pos++) {
      const val = variant[`option${pos}`];
      if (val === null || val === '') continue;
      const inputs = this.querySelectorAll(`input[data-option-position="${pos}"]`);
      for (const input of inputs) {
        input.checked = input.value === val;
      }
    }
  }

  /**
   * Human-readable description of the option values the shopper had selected (before sync).
   * @param {Map<number, string>} selectionMap
   * @returns {string}
   */
  #describeClickedCombination(selectionMap) {
    const positions = [...selectionMap.keys()]
      .filter((p) => Number.isFinite(p))
      .sort((a, b) => a - b);
    const parts = [];
    for (const pos of positions) {
      const val = selectionMap.get(pos);
      if (val === null || val === '') continue;
      const fieldset = this.querySelector(`fieldset.option[data-option-position="${pos}"]`);
      const name = fieldset?.dataset.optionName?.trim();
      parts.push(name ? `${name}: ${val}` : val);
    }
    return parts.join(', ');
  }

  /**
   * Sync radios and unavailable messaging from the resolved variant.
   * @param {HTMLInputElement} target
   * @param {number} changedPosition
   * @param {Map<number, string>} selectionMap
   * @returns {{ variantId: string | undefined; unavailableNotice: string | null }}
   */
  #prepareVariantChangeUI(target, changedPosition, selectionMap) {
    /** Shown again after `product-form` replaces the DOM on `variant:change`. */
    let unavailableNotice = null;

    if (!this.#variants.length || !Number.isFinite(changedPosition)) {
      this.#clearVariantUnavailableMessage();
      return { variantId: undefined, unavailableNotice };
    }

    const exact = this.#findExactVariant(selectionMap);
    const variant = this.#resolveVariant(selectionMap, changedPosition);

    if (!variant) {
      this.#clearVariantUnavailableMessage();
      return { variantId: undefined, unavailableNotice };
    }

    this.#syncInputsToVariant(variant);
    const variantId = String(variant.id);

    if (!exact) {
      const combination =
        this.#describeClickedCombination(selectionMap) || String(target.value ?? '');
      unavailableNotice = this.#formatUnavailableMessage(combination);
      this.#setVariantUnavailableMessage(unavailableNotice);
    } else {
      this.#clearVariantUnavailableMessage();
    }

    return { variantId, unavailableNotice };
  }

  /**
   * @param {string | undefined} variantId
   * @param {string | null} unavailableNotice
   * @param {HTMLInputElement} target
   */
  async #fetchAndPublishVariantChange(variantId, unavailableNotice, target) {
    const params = this.#createParams(variantId);

    try {
      PubSub.publish('variant:changing', { id: this.#productId });
      const html = await fetchHTML(`${this.#url}?${params}`, {
        signal: this.#abortController.signal,
      });

      PubSub.publish('variant:change', {
        id: this.#productId,
        data: { html, unavailableNotice },
      });

      if (this.#sectionId === 'fetch-quick-shop') return;

      this.#updateURL(variantId);
    } catch (error) {
      console.error('Error handling variant change:', error);
    } finally {
      this.#abortController = null;
      document.querySelector(`#${target.id}`)?.focus();
    }
  }

  /**
   * Handle variant change
   * @param {{ target: HTMLInputElement }} param
   */
  async #handleVariantChange({ target }) {
    if (this.#abortController) return;
    this.#abortController = new AbortController();

    const changedPosition = Number(target.dataset.optionPosition);
    const selectionMap = this.#getSelectedOptions();

    const prepared = this.#prepareVariantChangeUI(target, changedPosition, selectionMap);
    let variantId = prepared.variantId;
    if (variantId === null) variantId = target.dataset.variantId;
    await this.#fetchAndPublishVariantChange(variantId, prepared.unavailableNotice, target);
  }

  /**
   * Update the URL with the new variant ID
   * @param {string} variantId - The variant ID
   */
  #updateURL(variantId) {
    const { searchParams } = new URL(window.location);
    searchParams.set('variant', variantId);
    history.replaceState(null, '', `${window.location.pathname}?${searchParams}`);
  }

  /**
   * Create the parameters for the URL
   * @param {string} variantId - The variant ID
   * @returns {string} The parameters
   */
  #createParams(variantId) {
    return [`section_id=${this.#sectionId}`, `variant=${variantId}`].join('&');
  }

  /**
   * @returns {HTMLElement | null}
   */
  #getUnavailableLiveRegion() {
    return this.querySelector('[data-variant-unavailable-live]');
  }

  /**
   * @param {string} combination
   * @returns {string}
   */
  #formatUnavailableMessage(combination) {
    const template = this.dataset.i18nVariantUnavailable;
    if (!template) return '';
    const token = '[[combination]]';
    return template.includes(token) ? template.replaceAll(token, combination) : template;
  }

  /**
   * @param {string} text
   */
  #setVariantUnavailableMessage(text) {
    const el = this.#getUnavailableLiveRegion();
    if (!el || !text) return;
    el.textContent = text;
    el.classList.remove('hidden');
  }

  /**
   * Re-apply notice after the product form DOM is replaced (see `product-form.js`).
   * @param {string} text
   */
  showUnavailableNotice(text) {
    this.#setVariantUnavailableMessage(text);
  }

  #clearVariantUnavailableMessage() {
    const el = this.#getUnavailableLiveRegion();
    if (!el) return;
    el.replaceChildren();
    el.classList.add('hidden');
  }
}

customElements.define('product-variant-select', ProductVariantSelect);
